import {
  createContext,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { csrfToken } from "@/api/client";
import { CaptureManager } from "./manager";
const Context = createContext<CaptureManager | null>(null);
// A failed local commit keeps its bounded RAM tail in the original owner's
// worker across auth-screen transitions. Another account never receives it.
const registry = new Map<number, { manager: CaptureManager; users: number }>();
export function CaptureProvider({
  accountId,
  children,
}: {
  accountId: number;
  children: React.ReactNode;
}) {
  const qc = useQueryClient();
  const [manager, setManager] = useState<CaptureManager | null>(null);
  useEffect(() => {
    if (typeof Worker === "undefined" || !globalThis.indexedDB) {
      setManager(null);
      return;
    }
    let entry = registry.get(accountId);
    if (!entry) {
      entry = {
        manager: new CaptureManager(String(accountId), csrfToken, () => {
          void qc.invalidateQueries({ queryKey: ["studio-tasks"] });
          void qc.invalidateQueries({ queryKey: ["studio-task"] });
        }),
        users: 0,
      };
      registry.set(accountId, entry);
    }
    entry.users++;
    entry.manager.activate();
    setManager(entry.manager);
    const own = entry;
    return () => {
      own.users--;
      queueMicrotask(() => {
        if (own.users === 0)
          void own.manager.close().then((closed) => {
            if (closed && own.users === 0 && registry.get(accountId) === own)
              registry.delete(accountId);
          });
      });
    };
  }, [accountId, qc]);
  return <Context.Provider value={manager}>{children}</Context.Provider>;
}
export function useCaptureManager(): CaptureManager | null {
  return useContext(Context);
}
export function useCaptureSnapshot() {
  const manager = useCaptureManager();
  return useSyncExternalStore(
    manager?.subscribe ?? emptySubscribe,
    manager?.getSnapshot ?? emptySnapshot,
    emptySnapshot,
  );
}
const EMPTY = {
  captures: [],
  drafts: [],
  activeId: null,
  receivedFrames: 0,
  pendingLocalChunks: 0,
  busy: false,
  syncing: false,
  error: null,
};
const emptySnapshot = () => EMPTY;
const emptySubscribe = () => () => {};
